<template>
  <section class="document-sidebar">
    <document-sidebar-nav
      class="document-sidebar__nav"
      :sections="sections"
      :active-section-key="activeSectionKey"
      @button-click="onMenuButtonClick"
    />
    <div class="document-sidebar__wrapper">
      <header class="document-sidebar__header">
        <h2 class="home__title">{{ activeSection.title }}</h2>
      </header>
      <div
        class="document-sidebar__content"
        :class="{ _viewer: !activeSectionIsThumbnails }"
      >
        <div
          v-if="card && contentsActivated && activeSectionIsContents"
          class="document-contents"
        >
          <document-contents
            :currentView="currentView"
            :hasScan="card.hasFormatScan()"
            :hasHtml="card.hasFormatHtml()"
            @content-item-clicked="handleContentItemClicked"
          />
        </div>
        <document-info
          v-if="activeSectionIsCard"
          :card="card"
          @show-continuity="handleContentItemClicked({ view: 'card', hash: '#continuity' })"
          @show-annotation="handleContentItemClicked({ view: 'card', hash: '#annotation' })"
        />
        <document-scan-viewer
          v-if="pages.length && activeSectionIsThumbnails"
          :document-id="documentId"
          :pages="pages"
          :show-thumbnails="true"
          @page-click="onScanPageClick"
        />
        <document-history
          v-if="historyActivated && activeSectionIsHistory"
          :document-id="documentId"
          :load-status="loadStatus.history? loadStatus.history: false"
        />
        <document-discussions
          v-if="card && discussionActivated && activeSectionIsDiscussion"
          :document="card"
        />
        <document-revisions
          v-if="revisionsActivated && activeSectionIsRevisions"
          :document-id="documentId"
          :load-status="loadStatus.revisions? loadStatus.revisions: false"
        />
        <document-attachments
          v-if="attachmentsActivated && activeSectionIsAttachments"
          :document-id="documentId"
          :load-status="loadStatus.attachments? loadStatus.attachments: false"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';

import DocumentDiscussions from './DocumentDiscussions';
import DocumentHistory from './DocumentHistory';
import DocumentInfo from './DocumentInfo.vue';
import DocumentScanViewer from './../DocumentViewer/DocumentScanViewer';
import DocumentSidebarNav from './DocumentSidebarNav';
import DocumentContents from './DocumentContents';
import DocumentRevisions from './DocumentRevisions';
import DocumentAttachments from './DocumentAttachments';
import { ROUTE_DOCUMENT_PARAM_VIEW_SCAN } from '@src/routes';

export const SECTION_CARD = 'card';
export const SECTION_CONTENTS = 'contents';
export const SECTION_DISCUSSION = 'discussion';
export const SECTION_HISTORY = 'history';
export const SECTION_THUMBNAILS = 'thumbnails';
export const SECTION_REVISIONS = 'revisions';
export const SECTION_ATTACHMENTS = 'attachments';

export default {
  components: {
    DocumentDiscussions,
    DocumentHistory,
    DocumentInfo,
    DocumentScanViewer,
    DocumentSidebarNav,
    DocumentContents,
    DocumentRevisions,
    DocumentAttachments
  },

  props: {
    card: {
      required: true
    },
    /** @type {Object|<Array<ApiDocumentScanPage>>} */
    pages: {
      type: Array, // Object for IE
      default: () => []
    },
    currentView: {
      type: String,
      default: null
    },
    loadStatus: {
      type: Object
    }
  },

  data: () => ({
    sections: {
      contents: {
        title: 'Оглавление',
        icon: '#table-of-contents',
        disabled: true
      },
      thumbnails: {
        title: 'Страницы',
        icon: '#thumbnails',
        disabled: true
      },
      card: {
        title: 'О документе',
        icon: '#document',
        disabled: false
      },
      history: {
        title: 'История',
        icon: '#timeline',
        disabled: true
      },
      discussion: {
        title: 'Обсуждение',
        icon: '#comments',
        disabled: true
      },
      revisions: {
        title: 'Редакции',
        icon: '#revisions',
        disabled: true
      },
      attachments: {
        title: 'Приложения',
        icon: '#attachments',
        disabled: true
      }
    },

    activeSectionKey: SECTION_CARD,
    contentsActivated: false,
    discussionActivated: false,
    historyActivated: false,
    revisionsActivated: false,
    attachmentsActivated: false
  }),

  computed: {
    ...mapGetters({
      documentId: 'currentDocumentId',
      currentDocumentInfo: 'currentDocumentInfo'
    }),
    activeSection() {
      return this.sections[this.activeSectionKey];
    },
    activeSectionIsCard() {
      return this.activeSectionKey === SECTION_CARD;
    },
    activeSectionIsContents() {
      return this.activeSectionKey === SECTION_CONTENTS;
    },
    activeSectionIsDiscussion() {
      return this.activeSectionKey === SECTION_DISCUSSION;
    },
    activeSectionIsHistory() {
      return this.activeSectionKey === SECTION_HISTORY;
    },
    activeSectionIsThumbnails() {
      return this.activeSectionKey === SECTION_THUMBNAILS;
    },
    activeSectionIsRevisions() {
      return this.activeSectionKey === SECTION_REVISIONS;
    },
    activeSectionIsAttachments() {
      return this.activeSectionKey === SECTION_ATTACHMENTS;
    },
    currentViewIsScan() {
      return this.currentView === ROUTE_DOCUMENT_PARAM_VIEW_SCAN;
    }
  },

  watch: {
    card(card) {
      this.init(card);
    },

    activeSectionKey(activeSectionKey) {
      switch (activeSectionKey) {
        case SECTION_CONTENTS:
          this.contentsActivated = true;
          break;
        case SECTION_DISCUSSION:
          this.discussionActivated = true;
          break;
        case SECTION_HISTORY:
          this.historyActivated = true;
          break;
        case SECTION_REVISIONS:
          this.revisionsActivated = true;
          break;
        case SECTION_ATTACHMENTS:
          this.attachmentsActivated = true;
          break;
      }
    }
  },

  created() {
    this.init();
  },

  methods: {
    /**
     * @param {ApiDocumentCard} card
     */
    init() {
      let sections = this.sections;

      if (this.card) {
        let hasFormatScan = this.card.hasFormatScan(),
          hasFormatHtml = this.card.hasFormatHtml(),
          hasAccessFull = this.card.hasAccessFull();

        sections.contents.disabled =
          (!hasFormatScan && !hasFormatHtml) || !hasAccessFull;
        sections.thumbnails.disabled = !hasFormatScan || !hasAccessFull;
        sections.history.disabled = false;
        sections.discussion.disabled = this.card.isChange;
        sections.revisions.disabled = !this.card.hasRevisions;
        sections.attachments.disabled = !this.card.hasAttachments;
      } else {
        Object.keys(sections).forEach(key => {
          if (key !== SECTION_CARD) {
            sections[key].disabled = true;
          }
        });
      }

      // if (this.activeSection.disabled) {
      this.activeSectionKey = SECTION_CARD;
      // }
      this.contentsActivated = false;
      this.discussionActivated = false;
      this.historyActivated = false;
      this.revisionsActivated = false;
      this.attachmentsActivated = false;
    },
    onMenuButtonClick(sectionKey) {
      this.activeSectionKey = sectionKey;
      this.$emit('change-active-section', sectionKey);
    },
    onScanPageClick(pageIndex) {
      this.$emit('scan-page-click', pageIndex);
    },
    handleContentItemClicked(viewObj) {
      this.$emit('change-content-view', viewObj);
    }
  }
};
</script>
